import { useMemo, useState } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  Mail,
  Plus,
  Send,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  Subtitle,
  VBadge,
  VButton,
  VField,
  VInput,
  VSelect,
  VTextarea,
  cn,
} from "@/components/ui";
import { findUserByRole, useCurrentUser } from "@/domains/auth";
import { useCase } from "@/domains/cases";
import { useAssets } from "@/domains/assets";
import { assetTypeLabel } from "@/domains/assets";
import { useRequest } from "@/domains/requests";
import type { EmailMessage } from "@/domains/communication/api/types";
import {
  useReceivedEmailsByCase,
  useSentEmailsByCase,
  useTemplates,
} from "@/domains/communication/api/queries";
import { useSendEmail } from "@/domains/communication/api/mutations";
import { renderTemplate } from "@/domains/communication/components/templateRendering";

type CaseMailTabProps = {
  caseId: string;
};

function normalizeSubject(subject: string): string {
  return subject.replace(/^(re|fwd?):\s*/i, "").trim().toLowerCase();
}

function messageTimestamp(message: EmailMessage): string {
  return message.direction === "sent" ? message.email.sentAt : message.email.receivedAt;
}

function groupIntoThreads(
  messages: ReadonlyArray<EmailMessage>,
): ReadonlyArray<ReadonlyArray<EmailMessage>> {
  const byId = new Map<string, EmailMessage>();
  for (const m of messages) byId.set(m.email.id, m);

  const root = new Map<string, string>();
  const find = (id: string): string => {
    const next = root.get(id);
    if (!next || next === id) {
      root.set(id, id);
      return id;
    }
    const r = find(next);
    root.set(id, r);
    return r;
  };

  for (const m of messages) {
    if (m.direction === "received" && m.email.inReplyToId && byId.has(m.email.inReplyToId)) {
      const rootId = find(m.email.inReplyToId);
      root.set(m.email.id, rootId);
    } else {
      if (!root.has(m.email.id)) root.set(m.email.id, m.email.id);
    }
  }

  const subjectRoot = new Map<string, string>();
  for (const m of messages) {
    const key = normalizeSubject(m.email.subject);
    if (!subjectRoot.has(key)) {
      subjectRoot.set(key, find(m.email.id));
    } else {
      root.set(find(m.email.id), subjectRoot.get(key) ?? m.email.id);
    }
  }

  const grouped = new Map<string, Array<EmailMessage>>();
  for (const m of messages) {
    const r = find(m.email.id);
    const arr = grouped.get(r) ?? [];
    arr.push(m);
    grouped.set(r, arr);
  }
  return [...grouped.values()]
    .map((arr) =>
      [...arr].sort((a, b) => (messageTimestamp(a) < messageTimestamp(b) ? -1 : 1)),
    )
    .sort((a, b) => {
      const lastA = messageTimestamp(a[a.length - 1]);
      const lastB = messageTimestamp(b[b.length - 1]);
      return lastA < lastB ? 1 : -1;
    });
}

export function CaseMailTab({ caseId }: CaseMailTabProps): React.JSX.Element {
  const currentUser = useCurrentUser();
  const partner = findUserByRole("partner");
  const { data: caseFile } = useCase(caseId);
  const { data: request } = useRequest(caseFile?.requestId ?? "");
  const { data: assets } = useAssets();
  const { data: templates } = useTemplates();
  const { data: sent } = useSentEmailsByCase(caseId);
  const { data: received } = useReceivedEmailsByCase(caseId);
  const sendEmail = useSendEmail();

  const [composeOpen, setComposeOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [recipient, setRecipient] = useState(partner?.email ?? "");
  const [recipientName, setRecipientName] = useState(
    partner ? `${partner.firstName} ${partner.lastName}` : "",
  );
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const variables = useMemo<Record<string, string>>(() => {
    const empty: Record<string, string> = {};
    if (!caseFile) return empty;
    const linkedAssets = (assets ?? []).filter((a) => caseFile.assetIds.includes(a.id));
    const assetSummary = linkedAssets
      .map((a) => `${a.number} (${assetTypeLabel(a.type)})`)
      .join(", ");
    const result: Record<string, string> = {
      caseNumber: caseFile.number,
      caseTitle: caseFile.title,
      requestNumber: request?.number ?? "",
      requestTitle: request?.title ?? caseFile.title,
      requesterFirstName: request?.requester.firstName ?? "",
      address: caseFile.address,
      startDate: caseFile.startDate,
      endDate: caseFile.endDate,
      assetSummary,
      deadline: caseFile.endDate,
    };
    return result;
  }, [caseFile, request, assets]);

  const threads = useMemo<ReadonlyArray<ReadonlyArray<EmailMessage>>>(() => {
    const all: Array<EmailMessage> = [
      ...(sent ?? []).map((email) => ({ direction: "sent" as const, email })),
      ...(received ?? []).map((email) => ({ direction: "received" as const, email })),
    ];
    return groupIntoThreads(all);
  }, [sent, received]);

  const handleSelectTemplate = (id: string): void => {
    setTemplateId(id);
    const tpl = (templates ?? []).find((t) => t.id === id);
    if (!tpl) return;
    const rendered = renderTemplate(tpl, variables);
    setSubject(rendered.subject);
    setBody(rendered.body);
  };

  const handleSend = (): void => {
    if (!caseFile) return;
    sendEmail.mutate(
      {
        caseId,
        templateId: templateId || undefined,
        subject,
        body,
        to: [{ email: recipient, name: recipientName }],
        sentByName: `${currentUser.firstName} ${currentUser.lastName}`,
      },
      {
        onSuccess: () => {
          setSubject("");
          setBody("");
          setTemplateId("");
          setComposeOpen(false);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <CardTitle>Communicatie</CardTitle>
          <Subtitle>
            Volledige briefwisseling rond dit dossier — verzonden én ontvangen.
          </Subtitle>
        </div>
        <VButton onClick={() => setComposeOpen((v) => !v)}>
          <Plus className="size-4" aria-hidden />
          {composeOpen ? "Sluit opsteller" : "Nieuwe mail"}
        </VButton>
      </div>

      {composeOpen ? (
        <Card padding="lg" className="space-y-3">
          <CardTitle>Nieuwe mail opstellen</CardTitle>
          <VField label="Template" htmlFor="tpl">
            <VSelect
              id="tpl"
              value={templateId}
              onChange={(e) => handleSelectTemplate(e.target.value)}
            >
              <option value="">— Geen template —</option>
              {(templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </VSelect>
          </VField>
          <div className="grid gap-3 sm:grid-cols-2">
            <VField label="Aan (e-mail)" htmlFor="to">
              <VInput
                id="to"
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </VField>
            <VField label="Naam ontvanger" htmlFor="toName">
              <VInput
                id="toName"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </VField>
          </div>
          <VField label="Onderwerp" htmlFor="subject" required>
            <VInput id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </VField>
          <VField label="Bericht" htmlFor="body" required>
            <VTextarea
              id="body"
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </VField>
          <div className="flex justify-end gap-2">
            <VButton variant="ghost" onClick={() => setComposeOpen(false)}>
              Annuleren
            </VButton>
            <VButton
              onClick={handleSend}
              disabled={
                sendEmail.isPending ||
                subject.trim().length === 0 ||
                body.trim().length === 0 ||
                recipient.trim().length === 0
              }
            >
              <Send className="size-4" aria-hidden />
              {sendEmail.isPending ? "Verzenden…" : "Verzenden"}
            </VButton>
          </div>
        </Card>
      ) : null}

      {threads.length === 0 ? (
        <EmptyState
          title="Nog geen briefwisseling"
          description="Er werden nog geen e-mails verzonden of ontvangen op dit dossier."
        />
      ) : (
        <ul className="space-y-3">
          {threads.map((thread, idx) => (
            <Thread key={thread[0].email.id} thread={thread} defaultOpen={idx === 0} />
          ))}
        </ul>
      )}
    </div>
  );
}

type ThreadProps = {
  thread: ReadonlyArray<EmailMessage>;
  defaultOpen: boolean;
};

function Thread({ thread, defaultOpen }: ThreadProps): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const first = thread[0];
  const last = thread[thread.length - 1];
  const subject = first.email.subject.replace(/^(re|fwd?):\s*/i, "");
  const receivedCount = thread.filter((m) => m.direction === "received").length;
  const sentCount = thread.length - receivedCount;
  const lastTimestamp = messageTimestamp(last).slice(0, 16).replace("T", " ");

  return (
    <li className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left hover:bg-muted/40"
        aria-expanded={open}
      >
        <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <Body weight="semibold" className="truncate">
              {subject}
            </Body>
            <VBadge color="neutral">{thread.length} bericht{thread.length === 1 ? "" : "en"}</VBadge>
          </div>
          <Caption tone="muted">
            {sentCount} verzonden · {receivedCount} ontvangen · laatste op {lastTimestamp}
          </Caption>
        </div>
        <ChevronDown
          className={cn(
            "mt-1 size-4 shrink-0 text-muted-foreground transition-transform",
            open ? "rotate-180" : "rotate-0",
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <ul className="space-y-2 border-t border-border bg-muted/20 px-4 py-3">
          {thread.map((message) => (
            <ThreadMessage key={message.email.id} message={message} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

type ThreadMessageProps = {
  message: EmailMessage;
};

function ThreadMessage({ message }: ThreadMessageProps): React.JSX.Element {
  if (message.direction === "sent") {
    const { email } = message;
    return (
      <li className="rounded-md border border-border bg-card px-3 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-2">
            <ArrowUpRight className="size-3.5 text-primary-600" aria-hidden />
            <Body weight="semibold">Verzonden door {email.sentByName}</Body>
          </div>
          <Caption tone="muted">{email.sentAt.slice(0, 16).replace("T", " ")}</Caption>
        </div>
        <Caption tone="muted" className="mt-0.5">
          Aan {email.to.map((r) => `${r.name} <${r.email}>`).join(", ")}
        </Caption>
        {email.templateId ? <VBadge color="info">Template</VBadge> : null}
        <Body className="mt-2 whitespace-pre-wrap text-sm">{email.body}</Body>
      </li>
    );
  }
  const { email } = message;
  return (
    <li className="rounded-md border border-success-200 bg-success-50/40 px-3 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <ArrowDownLeft className="size-3.5 text-success-700" aria-hidden />
          <Body weight="semibold">Ontvangen van {email.from.name}</Body>
        </div>
        <Caption tone="muted">{email.receivedAt.slice(0, 16).replace("T", " ")}</Caption>
      </div>
      <Caption tone="muted" className="mt-0.5">
        Van {email.from.email}
      </Caption>
      <Body className="mt-2 whitespace-pre-wrap text-sm">{email.body}</Body>
    </li>
  );
}
